import { useState } from 'react';
import { Link } from 'react-router-dom';
import { reconcileEnvironmentRuntime, recoverComponentProtection, settleWordPressObjectCache } from '../../api';
import type { RecoverComponentInput } from '../../api';
import type { ComponentRuntime, ComponentRuntimeView, EnvironmentRuntimeView, RuntimeReconciliation } from '../../api';
import { Callout, Checkbox, Input, Radio } from '../primitives';
import { Field, LoadingRows, StatusBadge } from '../ui';
import { EligibilityBlockers } from './EligibilityBlockers';
import { PanelCard, QuietEmpty, SectionHeader } from './ui';
import { relativeTime } from './projectHealth';

const kindLabel: Record<string, string> = {
  magento: 'Magento backend',
  frontend: 'Storefront',
  wordpress: 'WordPress',
};

function seconds(value: number | undefined): string {
  if (!value || value <= 0) return '—';
  if (value < 90) return `${Math.round(value)} s`;
  if (value < 5400) return `${Math.round(value / 60)} min`;
  return `${(value / 3600).toFixed(1)} h`;
}

function short(sha: string | undefined): string {
  return sha ? sha.slice(0, 7) : '';
}

function releaseOf(runtime: ComponentRuntime | null | undefined, runtimes: ComponentRuntime[], id: string | null | undefined): string {
  if (runtime && runtime.id === id) return runtime.release_tag;
  return runtimes.find((r) => r.id === id)?.release_tag ?? '';
}

/**
 * RuntimePanel shows, per component, what serves now and what a deployment
 * is doing, as two separate facts: the serving badge never turns red because
 * a candidate failed, and a component in a protection window is never shown
 * as serving because another component (a storefront with cached pages)
 * still answers. Rollback eligibility, background-work ownership and the
 * measured timings of the newest deployment are one expansion away.
 */
export function RuntimePanel({
  runtime,
  loading,
  environmentId,
  settingsPath,
}: {
  runtime: EnvironmentRuntimeView | undefined;
  loading: boolean;
  environmentId: string;
  settingsPath: string;
}) {
  const [checking, setChecking] = useState(false);
  const [reconciliation, setReconciliation] = useState<RuntimeReconciliation | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);
  const blueGreen = runtime?.settings.strategy === 'blue_green';

  async function check() {
    setChecking(true);
    setCheckError(null);
    try {
      const res = await reconcileEnvironmentRuntime(environmentId);
      setReconciliation(res.reconciliation);
    } catch (err) {
      setCheckError(err instanceof Error ? err.message : 'The server could not be checked.');
    } finally {
      setChecking(false);
    }
  }

  return (
    <PanelCard labelledBy="overview-runtimes" testId="runtime-panel">
      <SectionHeader
        id="overview-runtimes"
        title="Runtimes"
        actions={
          blueGreen ? (
            <button type="button" className="button button--secondary button--small" onClick={check} disabled={checking}>
              {checking ? 'Checking the server' : 'Check against the server'}
            </button>
          ) : undefined
        }
      />
      {loading && !runtime ? (
        <LoadingRows label="Loading runtimes" rows={2} rowHeight="44px" />
      ) : !runtime ? (
        <QuietEmpty>The runtime state could not be read.</QuietEmpty>
      ) : !blueGreen ? (
        <Callout tone="info" title="Recreate strategy" testId="runtime-recreate">
          A deployment switches the release and restarts the component's services, and holds requests while the database changes.
          {runtime.capability.supported ? (
            <>
              {' '}
              Blue-green runtimes are available for this environment: <Link to={settingsPath}>review the adoption</Link> in Settings.
            </>
          ) : runtime.capability.reason || runtime.capability.blockers?.length ? (
            <>
              {' '}
              Blue-green runtimes are not available yet:
              <EligibilityBlockers blockers={runtime.capability.blockers} fallback={<> {runtime.capability.reason}.</>} testId="runtime-recreate-blockers" />
            </>
          ) : null}
        </Callout>
      ) : runtime.components.length === 0 ? (
        <QuietEmpty>No components placed</QuietEmpty>
      ) : (
        <ul className="runtime-list" aria-label="Components and their runtimes">
          {runtime.components.map((c) => (
            <RuntimeRow key={c.application_id} component={c} environmentId={environmentId} />
          ))}
        </ul>
      )}
      {checkError ? (
        <Callout tone="danger" title="The server could not be checked" role="alert">
          {checkError}
        </Callout>
      ) : null}
      {reconciliation ? <ReconciliationResult reconciliation={reconciliation} runtime={runtime} /> : null}
    </PanelCard>
  );
}

function RuntimeRow({ component: c, environmentId }: { component: ComponentRuntimeView; environmentId: string }) {
  const active = c.active ?? undefined;
  const ownerTag = c.workers.state === 'granted' ? releaseOf(active, c.recent, c.workers.owner_runtime_id) : '';
  return (
    <li className="runtime-list__row" data-testid={`runtime-${c.application_id}`}>
      <div className="runtime-list__identity">
        <strong>{c.name}</strong>
        <span className="type-meta">{kindLabel[c.kind] ?? c.kind}</span>
      </div>
      <div className="runtime-list__live">
        {active ? (
          <span>
            <code>{active.release_tag}</code> <span className="muted">{short(active.commit_sha)}</span>
          </span>
        ) : (
          <span className="muted">No runtime serves yet</span>
        )}
        <span className="type-meta">
          {active?.promoted_at ? `Promoted ${relativeTime(active.promoted_at)}, ` : ''}
          route generation {c.route.observed_generation}
          {c.route.observed_generation !== c.route.desired_generation ? ` (recorded ${c.route.desired_generation})` : ''}
        </span>
        {c.candidate ? (
          <span className="runtime-list__candidate">
            Candidate <code>{c.candidate.release_tag}</code> <StatusBadge kind="runtimeState" value={c.candidate.state} />
          </span>
        ) : null}
      </div>
      <div className="runtime-list__badges">
        <StatusBadge kind="runtimeServing" value={servingBadgeValue(c)} testId={`runtime-serving-${c.application_id}`} />
        <ServingHealthNote component={c} />
      </div>
      {c.protection.state ? (
        <Callout tone={c.protection.state === 'retained' ? 'danger' : 'warning'} title={c.protection.state === 'retained' ? 'Protection retained for recovery' : 'Protected update in progress'} className="runtime-list__wide">
          {c.protection.reason}
          {c.protection.duration_seconds ? ` Held for ${seconds(c.protection.duration_seconds)}.` : ''}
          {c.kind === 'magento' ? ' Storefronts keep serving pages that do not need the backend; cart, checkout and other backend calls are unavailable until it reopens.' : ''}
          {c.protection.state === 'retained' ? <RecoveryForm environmentId={environmentId} component={c} /> : null}
        </Callout>
      ) : null}
      {c.cache_settlement?.pending ? <CacheSettlementRetry environmentId={environmentId} component={c} /> : null}
      <details className="runtime-list__details runtime-list__wide">
        <summary>Rollback, background work and timings</summary>
        <dl className="fact-list">
          <div className="fact-list__row">
            <dt>Traffic rollback</dt>
            <dd>
              {c.rollback.allowed ? (
                <>
                  Possible to <code>{c.previous?.release_tag}</code>
                  {c.rollback.start_previous ? ' (it is stopped and is started and verified first)' : ''}. The database is never restored by a rollback.
                </>
              ) : (
                <ul className="runtime-list__reasons">
                  {(c.rollback.reasons ?? ['Not possible']).map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              )}
            </dd>
          </div>
          <div className="fact-list__row">
            <dt>Background work</dt>
            <dd>
              {c.workers.state === 'granted' ? (
                <>
                  Runs in <code>{ownerTag || 'the active runtime'}</code> only (lease epoch {c.workers.epoch})
                </>
              ) : (
                <>Paused: no runtime may run cron or queued jobs{c.workers.reason ? ` (${c.workers.reason.replace(/_/g, ' ')})` : ''}</>
              )}
            </dd>
          </div>
          {c.previous ? (
            <div className="fact-list__row">
              <dt>Previous runtime</dt>
              <dd>
                <code>{c.previous.release_tag}</code> <StatusBadge kind="runtimeState" value={c.previous.state} />
                {c.previous.state === 'draining' || c.previous.state === 'drained' ? (
                  <div className="type-meta" data-testid="runtime-previous-running">
                    Its containers still run without traffic{c.previous.last_error ? `: ${c.previous.last_error}` : '.'} The next deployment that needs its slot drains and stops it first.
                  </div>
                ) : null}
              </dd>
            </div>
          ) : null}
          <div className="fact-list__row">
            <dt>Newest deployment</dt>
            <dd>
              {seconds(c.timings.total_seconds)} in total, {c.timings.protected_seconds > 0 ? `${seconds(c.timings.protected_seconds)} protected` : 'no protected interval'}.{' '}
              <span className="type-meta">{c.timings.observed_request_failures_note}</span>
            </dd>
          </div>
          {c.timings.step_phases && c.timings.step_phases.length > 0 ? (
            <div className="fact-list__row">
              <dt>Measured stages</dt>
              <dd>
                <ul className="runtime-list__phases" data-testid="runtime-step-phases">
                  {c.timings.step_phases.map((p) => (
                    <li key={p.phase}>
                      {p.phase.replace(/_/g, ' ')}: {p.outcome}, {seconds(p.duration_seconds)}
                      {p.outcome !== 'succeeded'
                        ? p.steps
                            .filter((s) => s.outcome !== 'succeeded')
                            .map((s) => ` (${String(s.detail?.title ?? s.detail?.step_key ?? '')}${s.detail?.error ? `: ${String(s.detail.error)}` : s.detail?.warning ? `: ${String(s.detail.warning)}` : ''})`)
                            .join('')
                        : ''}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          ) : null}
          {c.timings.phases.length > 0 ? (
            <div className="fact-list__row">
              <dt>Phases</dt>
              <dd>
                <ul className="runtime-list__phases">
                  {c.timings.phases.map((p) => (
                    <li key={p.id}>
                      {p.phase.replace(/_/g, ' ')}: {p.outcome}
                      {p.duration_ms != null ? `, ${seconds(p.duration_ms / 1000)}` : ''}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          ) : null}
        </dl>
      </details>
      {c.warnings.length > 0 ? (
        <ul className="runtime-list__warnings runtime-list__wide" aria-label={`Warnings for ${c.name}`}>
          {c.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * The serving badge's value. The server's "serving" means a route sends
 * traffic to the active runtime and no health check disagrees; with no
 * current health check at all nothing agrees either, so the badge is the
 * neutral "not verified" and never the green "Serving".
 */
export function servingBadgeValue(c: Pick<ComponentRuntimeView, 'serving' | 'health'>): string {
  if (c.serving === 'serving' && (!c.health || c.health.status === 'unknown')) return 'unverified';
  return c.serving;
}

/**
 * ServingHealthNote says where the serving badge comes from: the
 * component's own current health checks (never another component's: a
 * storefront's cached pages say nothing about the backend), or, when none
 * has a current result, only the route record.
 */
function ServingHealthNote({ component: c }: { component: ComponentRuntimeView }) {
  const h = c.health;
  if (!h) {
    // Routed, with no health record at all: the badge says "not verified"; this says why.
    return c.serving === 'serving' ? (
      <span className="type-meta" data-testid={`runtime-health-${c.application_id}`}>
        Not verified by a current health check
      </span>
    ) : null;
  }
  if (h.status === 'unknown') {
    return (
      <span className="type-meta" data-testid={`runtime-health-${c.application_id}`}>
        Not verified by a current health check
      </span>
    );
  }
  return (
    <span className="type-meta" data-testid={`runtime-health-${c.application_id}`} title={h.reasons.join('\n') || undefined}>
      {h.status === 'healthy' ? 'Health checks pass' : h.reasons[0] ?? `Health ${h.status}`}
      {h.checked_at ? `, checked ${relativeTime(h.checked_at)}` : ''}
    </span>
  );
}

function ReconciliationResult({ reconciliation, runtime }: { reconciliation: RuntimeReconciliation; runtime: EnvironmentRuntimeView | undefined }) {
  const name = (id: string) => runtime?.components.find((c) => c.application_id === id)?.name ?? id;
  const drift = reconciliation.components.filter((c) => c.drift.length > 0);
  if (drift.length === 0) {
    return (
      <Callout tone="success" title="The server matches the record">
        Checked {relativeTime(reconciliation.observed_at)}
      </Callout>
    );
  }
  return (
    <Callout tone="warning" title="The server differs from the record">
      <ul className="runtime-list__reasons">
        {drift.flatMap((c) => c.drift.map((d) => <li key={c.application_id + d}>{`${name(c.application_id)}: ${d}`}</li>))}
      </ul>
      Nothing was changed; resume or resolve the deployment that owns the change before acting.
    </Callout>
  );
}

/**
 * RecoveryForm: the explicit recovery of a retained protection window. The
 * database is recovered first, outside Commerce Ops' automatic steps (the
 * deployment's checkpoint through the backup workflow, or a verified
 * manual fix); the operator states which, and only then is the component
 * reopened on its active runtime with background work given back to it.
 */
function RecoveryForm({ environmentId, component: c }: { environmentId: string; component: ComponentRuntimeView }) {
  const [resolution, setResolution] = useState<RecoverComponentInput['resolution'] | ''>('');
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  async function recover() {
    if (!resolution) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await recoverComponentProtection(environmentId, c.application_id, { resolution, note, confirm: confirmed });
      setResult({ ok: true, text: `Recovery started (run ${res.execution_id.slice(0, 8)}). ${c.name} reopens on ${c.active?.release_tag ?? 'its active runtime'} when it completes.` });
    } catch (err) {
      setResult({ ok: false, text: err instanceof Error ? err.message : 'The recovery could not be started.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="runtime-recovery" data-testid={`runtime-recovery-${c.application_id}`}>
      <p>
        Nothing reopens this automatically and no database is restored for you. Recover the database first, then say how:
      </p>
      <Radio
        name={`recovery-${c.application_id}`}
        checked={resolution === 'checkpoint_restored'}
        onChange={() => setResolution('checkpoint_restored')}
        label="I restored the deployment's checkpoint (Backups → restore); orders since then were handled."
      />
      <Radio
        name={`recovery-${c.application_id}`}
        checked={resolution === 'database_verified'}
        onChange={() => setResolution('database_verified')}
        label={<>I verified the database works with {c.active?.release_tag ?? 'the active release'} (the change completed or was reverted by hand).</>}
      />
      <Field label="What was done (kept in the audit log)">
        <Input type="text" value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <Checkbox
        checked={confirmed}
        onChange={(e) => setConfirmed(e.target.checked)}
        data-testid="recovery-confirm"
        label={<>The database now matches {c.active?.release_tag ?? 'the active release'}; reopen {c.name} and give it background work.</>}
      />
      <button type="button" className="button button--secondary button--small" disabled={!resolution || !confirmed || busy} onClick={recover}>
        Reopen after recovery
      </button>
      {result ? <p className={result.ok ? 'runtime-recovery__ok' : 'runtime-recovery__error'} role={result.ok ? 'status' : 'alert'}>{result.text}</p> : null}
    </div>
  );
}

/**
 * CacheSettlementRetry: a WordPress serving release still owes its final
 * object-cache settlement because the runtime traffic moved away from was
 * not confirmed stopped (or the flush failed). The site keeps serving; the
 * retry stops the old runtime first and then empties only the serving
 * release's namespace.
 */
function CacheSettlementRetry({ environmentId, component: c }: { environmentId: string; component: ComponentRuntimeView }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  async function retry() {
    setBusy(true);
    setResult(null);
    try {
      const res = await settleWordPressObjectCache(environmentId, c.application_id);
      setResult({ ok: true, text: `Settlement started (run ${res.execution_id.slice(0, 8)}).` });
    } catch (err) {
      setResult({ ok: false, text: err instanceof Error ? err.message : 'The settlement could not be started.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Callout tone="warning" title="Object-cache settlement pending" className="runtime-list__wide">
      <span data-testid={`runtime-cache-settlement-${c.application_id}`}>
        {c.cache_settlement?.reason} The site keeps serving {c.active?.release_tag ?? 'its active release'}; cached values may be older than the database until the settlement runs.
      </span>{' '}
      <button type="button" className="button button--secondary button--small" disabled={busy} onClick={retry}>
        Stop the old runtime and settle
      </button>
      {result ? <p className={result.ok ? 'runtime-recovery__ok' : 'runtime-recovery__error'} role={result.ok ? 'status' : 'alert'}>{result.text}</p> : null}
    </Callout>
  );
}
